import { useGetDOSReport, useGetEmptyDOSReport } from '@cdab/scania/qpr/hooks'
import { capitalizeFirstLetter } from '@cdab/utils'
import {
  TdsButton,
  TdsCheckbox,
  TdsMessage,
  TdsModal
} from '@scania/tegel-react'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyledDiv } from './audit-report-modal.styles'
import type {
  AuditReportData,
  AuditReportModalProps
} from './audit-report-modal.types'

export const AuditReportModal = forwardRef<
  HTMLTdsModalElement,
  AuditReportModalProps
>(function AuditReportModal({ auditId, dealerId }, ref) {
  const { t } = useTranslation('common')

  const [data, setData] = useState<AuditReportData>({
    viewCheckpoints: true,
    viewSummary: true,
    viewPerspectives: false,
    viewDeviations: false,
    viewNotes: false
  })

  const [
    getEmptyDOSReportState,
    clickGetEmptyDOSReportState,
    setEmptyDOSReportState
  ] = useGetEmptyDOSReport(6, dealerId ?? 0) //empty DOS version 5

  const [getDOSReportState, clickGetDOSReportState, setDOSReportState] =
    useGetDOSReport(auditId ?? 0) //DOS version 5

  const isDOSReport = auditId !== undefined
  const isLoading =
    getEmptyDOSReportState.status === 'loading' ||
    getDOSReportState.status === 'loading'

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return
    if (isDOSReport) {
      clickGetDOSReportState(
        data.viewSummary,
        data.viewCheckpoints,
        data.viewPerspectives,
        data.viewDeviations,
        data.viewNotes
      )
    } else {
      clickGetEmptyDOSReportState(
        data.viewSummary,
        data.viewCheckpoints,
        data.viewPerspectives
      )
    }
  }

  const onClose = useCallback(() => {
    if (ref && typeof ref !== 'function' && ref.current) {
      ref.current.closeModal()
    }
  }, [ref])

  const handleFormClose = async () => {
    if (isLoading) return
    onClose()
  }

  useEffect(() => {
    if (getEmptyDOSReportState.status === 'success') {
      setEmptyDOSReportState({ status: 'none', message: null })
      onClose()
    }
    if (getDOSReportState.status === 'success') {
      setDOSReportState({ status: 'none', message: null })
      onClose()
    }
  }, [t, getEmptyDOSReportState.status, getEmptyDOSReportState.message, setEmptyDOSReportState, getDOSReportState.status, setDOSReportState, onClose])

  return (
    <TdsModal ref={ref} size='md'>
      <StyledDiv slot='body' className='tds-u-mt3'>
        <TdsCheckbox
          checked={data.viewCheckpoints}
          onTdsChange={() =>
            setData({
              ...data,
              viewCheckpoints: !data.viewCheckpoints
            })
          }
        >
          <div slot='label'>
            {capitalizeFirstLetter(t('view-checkPoints', { ns: 'common' }))}
          </div>
        </TdsCheckbox>

        <TdsCheckbox
          checked={data.viewSummary}
          onTdsChange={() =>
            setData({
              ...data,
              viewSummary: !data.viewSummary
            })
          }
        >
          <div slot='label'>
            {capitalizeFirstLetter(t('view-summary', { ns: 'common' }))}
          </div>
        </TdsCheckbox>

        <TdsCheckbox
          checked={data.viewPerspectives}
          onTdsChange={() =>
            setData({
              ...data,
              viewPerspectives: !data.viewPerspectives
            })
          }
        >
          <div slot='label'>
            {capitalizeFirstLetter(t('view-perspectives', { ns: 'common' }))}
          </div>
        </TdsCheckbox>

        {isDOSReport && (
          <>
            <TdsCheckbox
              checked={data.viewDeviations}
              onTdsChange={() =>
                setData({
                  ...data,
                  viewDeviations: !data.viewDeviations
                })
              }
            >
              <div slot='label'>
                {capitalizeFirstLetter(t('view-deviations', { ns: 'common' }))}
              </div>
            </TdsCheckbox>

            <TdsCheckbox
              checked={data.viewNotes}
              onTdsChange={() =>
                setData({
                  ...data,
                  viewNotes: !data.viewNotes
                })
              }
            >
              <div slot='label'>
                {capitalizeFirstLetter(t('view-notes', { ns: 'common' }))}
              </div>
            </TdsCheckbox>
          </>
        )}
      </StyledDiv>
      <div
        slot='actions'
        className='tds-u-flex tds-u-gap2 tds-u-flex-dir-col tds-u-w-100 tds-u-pt0'
      >
        {getEmptyDOSReportState.status === 'error' && (
          <div className='tds-u-w-100'>
            <TdsMessage
              header={getEmptyDOSReportState.message}
              variant='error'
            />
          </div>
        )}
        <div className='tds-u-flex tds-u-gap1'>
          <TdsButton
            disabled={isLoading}
            text={capitalizeFirstLetter('generate')}
            onClick={handleFormSubmit}
          />
          <TdsButton
            disabled={isLoading}
            variant='secondary'
            onClick={handleFormClose}
            text={capitalizeFirstLetter(t('close'))}
          />
        </div>
      </div>
    </TdsModal>
  )
})
